// Estado "gravando", pixel a pixel de mockups/1c-celular-gravando.html: rótulo com o
// ponto vermelho, cronômetro mono grande, a onda ao vivo e os botões PAUSAR/ENCERRAR
// (o PAUSAR vira RETOMAR quando pausado). Acrescenta a faixa de aviso de interrupção,
// que o mockup não previa (auto-pausa quando a tela sai de foco).
//
// Desvios conscientes do texto do mockup (marcados no relatório): removido o rótulo
// "44,1 kHz" do rodapé (a taxa real depende do aparelho — seria número inventado).

import { corC, fonteC, btnPrimarioC, btnSecundarioC } from '../../styles/tokensCelular';
import { Onda } from './Onda';
import type { FaseGravacao } from './useGravador';
import type { MutableRefObject } from 'react';

interface Props {
  fase: FaseGravacao; // 'gravando' | 'pausado'
  segundos: number;
  interrompido: boolean;
  analyser: MutableRefObject<AnalyserNode | null>;
  onPausar: () => void;
  onRetomar: () => void;
  onEncerrar: () => void;
}

function mmss(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function Gravando({
  fase,
  segundos,
  interrompido,
  analyser,
  onPausar,
  onRetomar,
  onEncerrar,
}: Props): JSX.Element {
  const pausado = fase === 'pausado';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 20px 0',
          fontFamily: fonteC.mono,
          fontSize: 11,
          color: corC.muted,
          letterSpacing: '.08em',
        }}
      >
        <span>vmed</span>
        <span>tela travada p/ toque acidental</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '24px 20px 0' }}>
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: pausado ? corC.muted : corC.vermelho,
            display: 'block',
          }}
        />
        <span
          style={{
            fontFamily: fonteC.condensada,
            fontWeight: 600,
            fontSize: 17,
            letterSpacing: '.18em',
            color: corC.texto,
          }}
        >
          {pausado ? 'PAUSADO' : 'GRAVANDO'}
        </span>
        <div style={{ height: 1, flex: 1, background: corC.divisor }} />
      </div>

      <div
        style={{
          padding: '18px 20px 0',
          fontFamily: fonteC.mono,
          fontSize: 56,
          lineHeight: 1,
          letterSpacing: '.02em',
          color: corC.texto,
        }}
      >
        {mmss(segundos)}
      </div>
      <div style={{ padding: '6px 20px 0', fontSize: 12.5, color: corC.muted }}>
        {pausado ? 'Pausado. Toque em retomar para continuar.' : 'Áudio captando. Nada é transcrito nesta tela.'}
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '0 20px', minHeight: 0 }}>
        <Onda analyser={analyser} ativo={!pausado} />
      </div>

      <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {interrompido && (
          <div
            style={{
              fontSize: 12.5,
              lineHeight: 1.5,
              color: corC.avisoTexto,
              background: corC.avisoBg,
              border: `1px solid ${corC.avisoBorda}`,
              padding: '9px 11px',
            }}
          >
            A gravação foi pausada porque a tela saiu de foco — parte do áudio pode ter faltado.
            Confira e toque em <b>RETOMAR</b> para continuar, ou <b>ENCERRAR</b> para enviar o que já tem.
          </div>
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontFamily: fonteC.mono,
            fontSize: 10,
            letterSpacing: '.12em',
            color: corC.muted,
            borderTop: `1px solid ${corC.divisorFraco}`,
            paddingTop: 12,
          }}
        >
          <span>MIC INTERNO</span>
          <span>ARMAZENADO NO APARELHO</span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            onClick={pausado ? onRetomar : onPausar}
            style={{ ...btnSecundarioC, flex: 1, height: 60, fontSize: 19 }}
          >
            {pausado ? 'RETOMAR' : 'PAUSAR'}
          </button>
          <button
            type="button"
            onClick={onEncerrar}
            style={{ ...btnPrimarioC, flex: 2, height: 60, fontSize: 19 }}
          >
            ENCERRAR
          </button>
        </div>
      </div>
    </div>
  );
}
